import React, { useEffect, useRef, useState } from 'react';
import { ChatHeader, FileItem } from '../types/types';
import HeaderMessage from './HeaderMessage';
import RenderMessage from './RenderMessage';
import Tippy from '@tippyjs/react';
import data from '@emoji-mart/data';
import Picker from '@emoji-mart/react';
import fr from '@emoji-mart/data/i18n/fr.json';
import { Button } from 'components/common';
import { useAuthContext } from 'contexts/AuthContext';
import { useTranslation } from 'react-i18next';
import {
  DeleteChatMessageService,
  SendMessageService,
} from '../services/chatServices';
import {
  handelShowLastMessage,
  renderSentMessage,
} from '../utils/renderSentMessage';
import { getIconByExtension } from 'utils/globalMethodes';
import DetailsGroup from './DetailsGroup';
import { useMediaContext } from 'contexts/MediaContext';
import { toast } from 'react-toastify';
import { useDialog } from 'contexts/DialogContext';

interface MainChatContentProps {
  headerInfo: ChatHeader | null;
  isGroup: boolean;
  refresh: boolean;
  setRefresh: React.Dispatch<React.SetStateAction<boolean>>;
  setHeaderInof: React.Dispatch<React.SetStateAction<ChatHeader | null>>;
  resetGroupDetailsKey?: number;
  onBack?: () => void;
}
const MainChatContent: React.FC<MainChatContentProps> = ({
  headerInfo,
  isGroup,
  setRefresh,
  setHeaderInof,
  refresh,
  resetGroupDetailsKey,
  onBack,
}) => {
  const { t } = useTranslation();
  const { user } = useAuthContext();
  const inputFiles = useRef<HTMLInputElement | null>(null);
  const [selectedFiles, setSelectedFiles] = useState<FileItem[]>([]);
  const [showGroupDetail, setShowGroupDetail] = useState<null | boolean>(null);
  const [isSendMessage, setIsSendMessage] = useState<boolean>(false);
  const [isMultiline, setIsMultiline] = useState<boolean>(false);
  const inputMessage = useRef<HTMLTextAreaElement | null>(null);

  const handleMessageInput = (): void => {
    const el = inputMessage.current;
    if (!el) return;
    // Count rows against a fixed single-line baseline so the result is stable.
    el.style.lineHeight = '1.1';
    el.style.height = 'auto';
    const cs = getComputedStyle(el);
    const baseLine = parseFloat(cs.lineHeight) || 1;
    const padding = parseFloat(cs.paddingTop) + parseFloat(cs.paddingBottom);
    const rows = Math.round((el.scrollHeight - padding) / baseLine);
    const multiline = rows > 1;
    setIsMultiline(multiline);
    // Apply the final leading synchronously, then size the box (max 2 rows).
    el.style.lineHeight = multiline ? '1.4' : '1.1';
    el.style.height = 'auto';
    const lineHeight = parseFloat(getComputedStyle(el).lineHeight) || baseLine;
    const maxHeight = lineHeight * 2 + padding;
    el.style.height = `${Math.min(el.scrollHeight, maxHeight)}px`;
    el.style.overflowY = el.scrollHeight > maxHeight ? 'auto' : 'hidden';
  };
  const { openMedia } = useMediaContext();
  const dialog = useDialog();
  useEffect(() => {
    setShowGroupDetail(null);
  }, [headerInfo, resetGroupDetailsKey, refresh]);

  useEffect(() => {
    const dropZone = document.querySelector(
      '.chat-main-content',
    ) as HTMLDivElement | null;
    const containerDrag = document.querySelector(
      '.upload-chat-files',
    ) as HTMLDivElement | null;

    if (!dropZone || !containerDrag || !inputFiles.current) return;
    let dragCounter = 0;

    const handleDragEnter = (event: DragEvent): void => {
      event.preventDefault();
      dragCounter++;
      inputFiles.current!.classList.add(
        'tw-absolute',
        'tw-inset-0',
        'tw-z-50',
        'tw-opacity-0',
      );
      inputFiles.current!.style.display = 'block';
      containerDrag.classList.remove('tw-hidden');
    };

    const handleDragLeave = (event: DragEvent): void => {
      event.preventDefault();
      dragCounter--;
      if (dragCounter === 0) {
        inputFiles.current!.classList.remove(
          'tw-absolute',
          'tw-inset-0',
          'tw-z-50',
          'tw-opacity-0',
        );
        inputFiles.current!.style.display = 'none';
        containerDrag.classList.add('tw-hidden');
      }
    };

    dropZone.addEventListener('dragenter', handleDragEnter);
    dropZone.addEventListener('dragleave', handleDragLeave);
    dropZone.addEventListener('drop', (event: DragEvent) => {
      setTimeout(() => {
        handleDragLeave(event);
      }, 100);
    });
    return (): void => {
      dropZone.removeEventListener('dragenter', handleDragEnter);
      dropZone.removeEventListener('dragleave', handleDragLeave);
      dropZone.removeEventListener('drop', handleDragLeave);
    };
  }, []);

  const handelSendMessage = async (): Promise<void> => {
    try {
      setIsSendMessage(true);
      if (!headerInfo) return;
      const formData = new FormData();

      selectedFiles.forEach((file) => {
        formData.append('files[]', file.file);
      });
      if (inputMessage.current && inputFiles.current && headerInfo) {
        const formData = new FormData();
        formData.append('recieverId', headerInfo.id);
        formData.append('isGroup', String(isGroup));
        formData.append('message', inputMessage.current.value);

        if (selectedFiles.length > 0) {
          selectedFiles.forEach((file) => {
            formData.append('files', file.file);
          });
        }

        const response = await SendMessageService(formData);
        if (response.success) {
          inputMessage.current.value = '';
          inputMessage.current.style.height = 'auto';
          inputMessage.current.style.lineHeight = '1.1';
          inputMessage.current.style.overflowY = 'hidden';
          setIsMultiline(false);
          setSelectedFiles([]);
          renderSentMessage({
            item: response.data,
            user,
            handeldeleteMessage,
            openMedia: openMedia,
          });
          const userCard = document.querySelector(
            `.chat-user-item[data-id="${response.data.type}"]`,
          );
          if (userCard) {
            handelShowLastMessage(
              userCard as HTMLDivElement,
              response.data.message ??
                response.data.files[response.data.files.length]?.name,
              isGroup,
            );
          }
        } else {
          toast.error(response.message);
        }
      }
    } finally {
      setIsSendMessage(false);
    }
  };

  const handleFileChange = (): void => {
    const files = inputFiles.current?.files;
    if (files) {
      const newFiles = Array.from(files).map((file, index) => ({
        id: index + 1,
        name: file.name,
        size: file.size,
        type: file.type,
        file: file,
      }));
      setSelectedFiles((prevFiles) => [...prevFiles, ...newFiles]);
    }
  };

  const handleDeleteFile = (index: number): void => {
    setSelectedFiles((prevFiles) =>
      prevFiles.filter((file, idx) => idx !== index),
    );

    if (inputFiles.current) {
      const dataTransfer = new DataTransfer();
      Array.from(inputFiles.current.files || []).forEach((file, idx) => {
        if (idx !== index) {
          dataTransfer.items.add(file);
        }
      });
      inputFiles.current.files = dataTransfer.files;
    }
  };

  const handeldeleteMessage = (id: number): void => {
    dialog
      .delete('Chat.message.delete.title', 'Chat.message.delete.description')
      .then(async (res) => {
        if (res) {
          const response = await DeleteChatMessageService(id);
          if (response.success) {
            toast.success(t('Default.succesMessage'));
            document
              .querySelector(`.minthr-chat-sent[data-id="${id}"]`)
              ?.remove();

            document
              .querySelector(`.minthr-chat-received[data-id="${id}"]`)
              ?.remove();
          } else {
            toast.error(response.message);
          }
        }
      });
  };

  return headerInfo ? (
    <>
      {isGroup && showGroupDetail && (
        <DetailsGroup
          setShowGroupDetail={setShowGroupDetail}
          groupId={Number(headerInfo.id)}
          showGroupDetail={showGroupDetail}
        />
      )}

      {!showGroupDetail && (
        <>
          <div className="tw-shrink-0 tw-flex tw-items-center tw-justify-between tw-gap-2 tw-px-2.5 tw-py-1.5 tw-border-b tw-border-light-gray tw-bg-white">
            <HeaderMessage
              headerInfo={headerInfo}
              setShowGroupDetail={setShowGroupDetail}
              setRefresh={setRefresh}
              setHeaderInof={setHeaderInof}
              onBack={onBack}
            />
          </div>

          <div className="chat-main-content tw-flex tw-flex-1 tw-flex-col tw-min-h-0 tw-relative tw-bg-[rgba(248,250,252,0.7)]">
            <div className="tw-relative tw-flex tw-flex-1 tw-flex-col tw-min-h-0">
              <RenderMessage
                headerInfo={headerInfo}
                handeldeleteMessage={handeldeleteMessage}
              />

              <div className="tw-hidden upload-chat-files tw-pointer-events-none tw-absolute tw-inset-3 tw-rounded-2xl tw-bg-primary-opacity tw-backdrop-blur-sm tw-border-2 tw-border-dashed tw-border-primary tw-flex tw-flex-col tw-items-center tw-justify-center tw-gap-2 tw-z-20 upload">
                <img
                  className="tw-w-10 tw-h-10 tw-icon-primary"
                  src="/icons/upload.svg"
                  alt="Upload Icon"
                />
                <div className="tw-text-primary tw-font-medium tw-text-sm">
                  {t('DragAndDrop.first', { defaultValue: 'Drop' })}{' '}
                  {t('DragAndDrop.lastCv', { defaultValue: 'files here' })}
                </div>
              </div>
            </div>

            <input
              type="file"
              className="chat-input-file"
              multiple
              id="Files"
              name="Files"
              style={{ display: 'none' }}
              ref={inputFiles}
              onChange={handleFileChange}
            />

            <div className="tw-shrink-0 tw-border-t tw-border-light-gray tw-bg-white tw-px-3 tw-py-2.5 ">
              {selectedFiles.length > 0 && (
                <div className="tw-flex tw-flex-wrap tw-gap-2 tw-mb-2">
                  {selectedFiles.map((item, idx) => (
                    <div
                      key={idx}
                      className="tw-inline-flex tw-items-center tw-gap-1 tw-bg-light-gray2 tw-rounded-full tw-px-2 tw-py-1 tw-text-[calc(var(--unit-minthr)*10)] tw-text-dark"
                    >
                      <img
                        className="tw-w-4 tw-h-4 tw-shrink-0"
                        src={`/icons/${getIconByExtension(item.name)}`}
                        alt="file icon"
                      />
                      <span className="tw-max-w-[calc(var(--unit-minthr)*140)] tw-truncate">
                        {item.name}
                      </span>
                      <button
                        type="button"
                        onClick={() => handleDeleteFile(idx)}
                        className="tw-flex tw-items-center tw-justify-center tw-w-5 tw-h-5 tw-rounded-full tw-bg-light-gray3 hover:tw-bg-danger hover:tw-text-white tw-border-none tw-cursor-pointer tw-transition"
                        aria-label={t('chat.removeFile', {
                          defaultValue: 'Remove',
                        })}
                      >
                        <img
                          className="tw-w-3 tw-h-3"
                          src="/icons/delete.svg"
                          alt=""
                        />
                      </button>
                    </div>
                  ))}
                </div>
              )}

              <div
                id="textAreaWraper"
                className="tw-flex tw-items-end tw-gap-2"
              >
                <label
                  htmlFor="Files"
                  id="fileButton"
                  className="tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9 tw-rounded-md tw-bg-transparent tw-border tw-border-light-gray hover:tw-bg-light-gray2 tw-cursor-pointer tw-shrink-0 tw-transition"
                  title={t('chat.attach', { defaultValue: 'Attach' })}
                  aria-label={t('chat.attach', { defaultValue: 'Attach' })}
                >
                  <img
                    className="tw-w-[calc(var(--unit-minthr)*18)] tw-h-[calc(var(--unit-minthr)*18)] tw-icon-gray"
                    src="/icons/attachment.svg"
                    alt=""
                  />
                </label>

                <div className="tw-flex tw-flex-1 tw-items-end tw-gap-1 tw-bg-[#f6f8f7] tw-rounded-[calc(var(--unit-minthr)*12)] tw-px-3 tw-py-1 tw-border tw-border-transparent tw-transition tw-duration-200 focus-within:tw-bg-white focus-within:tw-border-primary focus-within:tw-ring-2 focus-within:tw-ring-primary-opacity focus-within:tw-shadow-sm">
                  <textarea
                    id="emoji-textarea"
                    className={`tw-flex-1 tw-bg-transparent tw-border-none tw-outline-none tw-text-[calc(var(--unit-minthr)*11)] tw-text-dark ${isMultiline ? 'tw-leading-[1.4]' : 'tw-leading-[1.1]'} tw-resize-none tw-overflow-hidden chat-message-input tw-placeholder:tw-text-dark-gray4 `}
                    placeholder={t('chat.write.message', {
                      defaultValue: 'Message...',
                    })}
                    style={{
                      minHeight: 'calc(var(--unit-minthr) * 18)',
                      resize: 'none',
                    }}
                    ref={inputMessage}
                    onInput={handleMessageInput}
                  />
                  <Tippy
                    trigger="click"
                    interactive={true}
                    content={
                      <div className="chat-emoji-picker">
                        <Picker
                          local={fr}
                          i18n={fr}
                          data={data}
                          categories={[
                            'people',
                            'nature',
                            'foods',
                            'activity',
                            'objects',
                            'symbols',
                          ]}
                          perLine={8}
                          emojiSize={20}
                          emojiButtonSize={28}
                          maxFrequentRows={1}
                          previewPosition="none"
                          skinTonePosition="none"
                          navPosition="top"
                          searchPosition="sticky"
                          theme="light"
                          onEmojiSelect={(e: { native: unknown }) => {
                            if (inputMessage.current) {
                              inputMessage.current.value += e.native;
                            }
                          }}
                        />
                      </div>
                    }
                    arrow={false}
                    className="transparent-tooltip tw-absolute tw-bottom-0 tw--right-9 "
                  >
                    <button
                      type="button"
                      id="emoji-button"
                      className="tw-flex tw-items-center tw-justify-center tw-w-7 tw-h-7 tw-rounded-md tw-bg-transparent hover:tw-bg-light-gray2   tw-border-none tw-shrink-0 tw-cursor-pointer tw-transition"
                      title={t('chat.emoji', { defaultValue: 'Emoji' })}
                    >
                      <img
                        className="tw-w-[calc(var(--unit-minthr)*16)] tw-h-[calc(var(--unit-minthr)*16)] tw-icon-gray"
                        src="/icons/smile.svg"
                        alt=""
                      />
                    </button>
                  </Tippy>
                </div>

                <Button
                  loading={isSendMessage}
                  type="button"
                  className="tw-flex tw-items-center tw-justify-center tw-w-9 tw-h-9  tw-cursor-pointer tw-shrink-0 tw-transition tw-rounded-md tw-bg-primary hover:tw-bg-primary-dark tw-text-white tw-border-none tw-shrink-0 tw-transition disabled:tw-opacity-60 chat-send"
                  onClick={handelSendMessage}
                  aria-label={t('chat.send', { defaultValue: 'Send' })}
                >
                  <img
                    className="tw-w-[calc(var(--unit-minthr)*16)] tw-h-[calc(var(--unit-minthr)*16)] tw-icon-white"
                    src="/icons/send.svg"
                    alt=""
                  />
                </Button>
              </div>
            </div>
          </div>
        </>
      )}
    </>
  ) : null;
};

export default MainChatContent;
